export interface Box { left: number; top: number; right: number; bottom: number }

/** What a menu opens from: its button, the cursor (a context menu), or the row of the menu it continues (a submenu). */
export type MenuAnchor = { kind: 'button'; rect: Box; align: 'start' | 'end' } | { kind: 'point'; x: number; y: number } | { kind: 'side'; rect: Box };

/** The row padding of a menu panel: a submenu rises by it so that its first row is level with the row that opened it. */
const PANEL_PADDING = 5;

/**
 * One axis: the preferred side if the menu fits there, else the other if it fits, else the roomier one; then held inside
 * `[min, max]`. `forward` is where a menu growing toward larger coordinates starts, `backward` where one growing back ends.
 */
function side(forward: number, backward: number, length: number, min: number, max: number, preferForward: boolean): { at: number; forward: boolean } {
  const fitsForward = forward + length <= max, fitsBackward = backward - length >= min;
  const goForward = preferForward ? fitsForward || (!fitsBackward && max - forward >= backward - min) : !(fitsBackward || (!fitsForward && backward - min >= max - forward));
  const at = goForward ? forward : backward - length;
  return { at: Math.max(min, Math.min(at, max - length)), forward: goForward };
}

export function placeMenu(anchor: MenuAnchor, size: { width: number; height: number }, bounds: Box, gap = 6): { left: number; top: number; origin: string } {
  const horizontal = anchor.kind === 'point' ? side(anchor.x, anchor.x, size.width, bounds.left, bounds.right, true)
    : anchor.kind === 'side' ? side(anchor.rect.right + gap, anchor.rect.left - gap, size.width, bounds.left, bounds.right, true)
      : side(anchor.rect.left, anchor.rect.right, size.width, bounds.left, bounds.right, anchor.align === 'start');
  const vertical = anchor.kind === 'point' ? side(anchor.y, anchor.y, size.height, bounds.top, bounds.bottom, true)
    : anchor.kind === 'side' ? { at: Math.max(bounds.top, Math.min(anchor.rect.top - PANEL_PADDING, bounds.bottom - size.height)), forward: true }
      : side(anchor.rect.bottom + gap, anchor.rect.top - gap, size.height, bounds.top, bounds.bottom, true);
  return { left: horizontal.at, top: vertical.at, origin: `${horizontal.forward ? 'left' : 'right'} ${vertical.forward ? 'top' : 'bottom'}` };
}
